import { useRef } from "react";
import { Box, Typography, InputAdornment, Stack } from "@mui/material";
import {
  Form,
  BooleanInput,
  NumberInput,
  TextInput,
  SelectInput,
  DateInput,
  SaveButton,
  maxValue,
  minValue,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { useAppTheme } from "@/hooks/useAppTheme";
import { FormSection } from "@/components/common/inputs/FormSection";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import type { EndorsementType } from "@/types";
import { getFieldSchema, hasJunctionSchema, type FieldSchema } from "./endorsementFieldSchemas";

import { JunctionFieldsCreate } from "./JunctionFieldsCreate";

// ─────────────────────────────────────────────────────────────
// Numeric limits — aligned to PostgreSQL column types
// ─────────────────────────────────────────────────────────────
/** NUMERIC(12,2) — max 10 digits before decimal */
const DOLLAR_MAX = 9_999_999_999.99;
/** NUMERIC(5,4) stored as 0–1 decimal, but users enter 0–100 display % */
const PERCENT_MAX = 1;
/** INT4 range */
const INTEGER_MAX = 2_147_483_647;

interface EndorsementConfigFormProps {
  endorsementType: EndorsementType;
  /** Pre-populated values for edit mode, or defaults for create mode */
  defaultValues?: Record<string, unknown>;
  /** Called with the full form data (common fields + config) */
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  submitLabel?: string;
  /** Policy ID — needed for ClaimIdPicker and other junction field components */
  policyId?: string;
  /** When true, renders junction data fields (codes, conditions, tiers, etc.) below the config section */
  showJunctionFields?: boolean;
}

/**
 * Generic schema-driven form for endorsement configuration.
 *
 * Reads the field schema registry for the given endorsement type and renders
 * the appropriate react-admin inputs. Handles common fields (enabled, valid period)
 * above the type-specific config section.
 *
 * Used by both EndorsementCreatePanel and EndorsementEditPanel.
 */
export const EndorsementConfigForm = ({
  endorsementType,
  defaultValues = {},
  onSubmit,
  onCancel,
  submitLabel = "Save",
  policyId,
  showJunctionFields = false,
}: EndorsementConfigFormProps) => {
  const { theme } = useAppTheme();
  const schema = getFieldSchema(endorsementType);
  const getJunctionDataRef = useRef<(() => Record<string, unknown>) | null>(null);
  const showJunctions = showJunctionFields && hasJunctionSchema(endorsementType);

  // Build form default values: common fields + type-specific config
  const formDefaults = {
    is_enabled: true,
    valid_period_start: null as string | null,
    valid_period_end: null as string | null,
    ...defaultValues,
  };

  // react-admin's <Form> inside a <Show> merges RecordContext (the policy record)
  // into submitted data. Filter to only fields the form actually declares so the
  // full policy record doesn't leak into the endorsement payload.
  const allowedKeys = new Set([
    "is_enabled",
    "valid_period_start",
    "valid_period_end",
    ...(schema?.map((f) => f.source) ?? []),
  ]);

  const handleSubmit = (data: Record<string, unknown>) => {
    const filtered = Object.fromEntries(Object.entries(data).filter(([key]) => allowedKeys.has(key)));
    // Merge junction data (managed outside react-admin form state) after the allowedKeys filter
    const junctionData = getJunctionDataRef.current?.() ?? {};
    onSubmit({ ...filtered, ...junctionData });
  };

  // No schema defined for this type — show placeholder
  if (schema === undefined) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Configuration for this endorsement type is not yet available.
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
          This type will be configurable once the backend schema is finalized.
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button variant={ButtonStyleType.Outlined} size={UIComponentSize.Small} onClick={onCancel}>
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Form defaultValues={formDefaults} record={{}} onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", pb: 10 }}>
        {/* Common fields */}
        <FormSection title="General">
          <BooleanInput source="is_enabled" label="Enabled" />
          <Stack direction="row" spacing={2}>
            <DateInput source="valid_period_start" label="Valid From" />
            <DateInput source="valid_period_end" label="Valid Until" />
          </Stack>
        </FormSection>

        {/* Type-specific config fields */}
        {schema.length > 0 ? (
          <FormSection title="Configuration">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {schema.map((field) =>
                field.showWhen ? (
                  <ConditionalField key={field.source} field={field} watchSource={field.showWhen} />
                ) : (
                  <SchemaField key={field.source} field={field} />
                )
              )}
            </Box>
          </FormSection>
        ) : !showJunctions ? (
          <Box sx={{ py: 3, px: 1 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              This endorsement type is event-driven only — no additional configuration fields.
            </Typography>
          </Box>
        ) : null}

        {/* Junction data fields (codes, conditions, tiers, entries, etc.) */}
        {showJunctions && policyId && (
          <JunctionFieldsCreate
            endorsementType={endorsementType}
            policyId={policyId}
            getJunctionDataRef={getJunctionDataRef}
          />
        )}
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: "background.paper",
          borderTop: `1px solid ${theme.palette.divider}`,
          px: 2.5,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          zIndex: 1,
        }}
      >
        <Button variant={ButtonStyleType.Outlined} size={UIComponentSize.Small} onClick={onCancel}>
          Cancel
        </Button>
        <SaveButton label={submitLabel} alwaysEnable />
      </Box>
    </Form>
  );
};

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

/**
 * AB#4643 review #16647: parse for the `dollar` NumberInput.
 *
 * react-admin's default parse on an empty NumberInput is `parseFloat("")` → `NaN`,
 * which JSON-serializes as `null` but leaves `NaN` in form state — fragile to
 * downstream consumers (validators, watchers, derived UI). For fields whose
 * schema declares `defaultValue: null` (i.e., the field is a nullable toggle —
 * see `annual_attachment_override`), an empty input should emit a literal
 * `null` instead. The full-replace PUT contract on the BE treats explicit
 * `null` as "clear the override; revert to formula" (verified by Bruno test
 * 9-clear-endorsement-override.bru on coronis-apps PR #3552).
 *
 * For fields whose schema declares `defaultValue: 0` (or any non-null), an
 * empty input emits `0` — matching the existing pattern for those fields.
 */
export const parseDollarInput = (raw: string, allowNull: boolean): number | null => {
  if (raw === "" || raw === null || raw === undefined) {
    return allowNull ? null : 0;
  }
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? (allowNull ? null : 0) : parsed;
};

/** Build react-admin validators for a numeric field */
export const buildNumericValidators = (field: FieldSchema) => {
  const validators = [];
  const fieldMin = field.min ?? 0;
  // `max: null` opts out of any upper bound; `undefined` falls through to type defaults.
  const capDisabled = field.max === null;
  let fieldMax: number | undefined = capDisabled ? undefined : (field.max ?? undefined);

  if (!capDisabled && fieldMax === undefined) {
    switch (field.type) {
      case "dollar":
        fieldMax = DOLLAR_MAX;
        break;
      case "percent":
        fieldMax = PERCENT_MAX;
        break;
      case "number":
        fieldMax = INTEGER_MAX;
        break;
    }
  }

  validators.push(minValue(fieldMin, `Must be at least ${fieldMin}`));

  if (fieldMax !== undefined) {
    // For percent fields, the stored value is 0–1 but display is 0–100.
    // react-admin validators run against the stored (parsed) value.
    const displayMax =
      field.type === "percent"
        ? `${fieldMax * 100}%`
        : field.type === "dollar"
          ? `$${fieldMax.toLocaleString()}`
          : fieldMax.toLocaleString();
    validators.push(maxValue(fieldMax, `Must be at most ${displayMax}`));
  }

  return validators;
};

/** Renders a single form field based on its schema */
const SchemaField = ({ field }: { field: FieldSchema }) => {
  const commonProps = {
    source: field.source,
    label: field.label,
    helperText: field.helperText,
    fullWidth: true,
    isRequired: field.required,
  };

  switch (field.type) {
    case "boolean":
      return <BooleanInput {...commonProps} />;
    case "number":
      return <NumberInput {...commonProps} validate={buildNumericValidators(field)} />;
    case "dollar":
      return (
        <NumberInput
          {...commonProps}
          validate={buildNumericValidators(field)}
          parse={(v: string) => parseDollarInput(v, field.defaultValue === null)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      );
    case "percent":
      return (
        <NumberInput
          {...commonProps}
          validate={buildNumericValidators(field)}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          format={(v: number) => (v !== null && v !== undefined ? parseFloat((v * 100).toPrecision(10)) : "")}
          parse={(v: string) => (v !== "" ? parseFloat((parseFloat(v) / 100).toPrecision(10)) : null)}
        />
      );
    case "text":
      return <TextInput {...commonProps} />;
    case "date":
      return <DateInput {...commonProps} />;
    case "select":
      return <SelectInput {...commonProps} choices={field.choices ?? []} />;
    default:
      return <TextInput {...commonProps} />;
  }
};

/** Wrapper that only renders a field when its controlling boolean is truthy */
const ConditionalField = ({ field, watchSource }: { field: FieldSchema; watchSource: string }) => {
  const watchValue = useWatch({ name: watchSource });

  if (!watchValue) return null;

  return (
    <Box pl={3}>
      <SchemaField field={field} />
    </Box>
  );
};
